import { defineStore } from 'pinia';
import { useOrgStore } from '@/stores/org';

/**
 * The list configurations someone saved: filters, advanced query and sort, under
 * a name.
 *
 * A view holds the route query verbatim, so opening one is a `router.push` and
 * nothing else — there is no second filtering path that could disagree with the
 * URL someone types by hand.
 *
 * Cached per list for the session, like the grid preferences: a list mounts and
 * unmounts constantly, and its views change only when someone saves one.
 */
export const useSavedViewsStore = defineStore('savedViews', {
    state: () => ({
        // listKey => [{ id, name, definition, is_default, … }]
        views: {},
        loading: {}
    }),

    getters: {
        forList: state => listKey => state.views[listKey] || [],
        defaultFor: state => listKey => (state.views[listKey] || []).find(view => view.is_default) || null
    },

    actions: {
        async load(listKey, { force = false } = {}) {
            const orgStore = useOrgStore();

            if (!listKey || !orgStore.api) {
                return [];
            }

            if (!force && this.views[listKey]) {
                return this.views[listKey];
            }

            if (this.loading[listKey]) {
                return this.loading[listKey];
            }

            const api = orgStore.api;
            const request = axios
                .get(`${api}/saved-views`, { params: { list_key: listKey } })
                .then(({ data }) => {
                    // Answered for an organization since left: not this one's views.
                    if (orgStore.api !== api) return [];
                    this.views = { ...this.views, [listKey]: data };

                    return data;
                })
                .catch(() => [])
                .finally(() => {
                    delete this.loading[listKey];
                });

            this.loading[listKey] = request;

            return request;
        },

        /** Forget the views read for the organization being left. */
        reset() {
            this.views = {};
            this.loading = {};
        },

        /**
         * Take an answer already on its way — the list bootstrap's. It stands in
         * for the in-flight request `load()` shares; a part left out (null) falls
         * back to the endpoint.
         *
         * @param {string} listKey
         * @param {Promise<Array|null>} pending
         */
        prime(listKey, pending) {
            if (!listKey || this.views[listKey] || this.loading[listKey]) {
                return;
            }

            this.loading[listKey] = pending
                .then(data => {
                    if (data == null) {
                        throw new Error('saved views left out of the bootstrap');
                    }
                    this.views = { ...this.views, [listKey]: data };
                    delete this.loading[listKey];

                    return data;
                })
                .catch(() => {
                    delete this.loading[listKey];

                    return this.load(listKey);
                });
        },

        async save(listKey, { name, definition, isDefault = false }) {
            const orgStore = useOrgStore();

            const { data } = await axios.post(`${orgStore.api}/saved-views`, {
                list_key: listKey,
                name,
                definition,
                is_default: isDefault
            });

            await this.load(listKey, { force: true });

            return data;
        },

        async update(view, changes) {
            const orgStore = useOrgStore();

            const { data } = await axios.put(`${orgStore.api}/saved-views/${view.id}`, {
                name: changes.name ?? view.name,
                definition: changes.definition ?? view.definition,
                is_default: changes.isDefault ?? view.is_default
            });

            await this.load(view.list_key, { force: true });

            return data;
        },

        /** The order someone dragged their views into, saved in one call. */
        async reorder(listKey, ids) {
            const orgStore = useOrgStore();

            // Answer from the order just set rather than from a refetch: the list
            // is already correct on screen, and a reload would make it jump.
            const byId = new Map((this.views[listKey] || []).map(view => [String(view.id), view]));
            this.views = { ...this.views, [listKey]: ids.map(id => byId.get(String(id))).filter(Boolean) };

            await axios.put(`${orgStore.api}/saved-views/reorder`, { list_key: listKey, ids });
        },

        async remove(view) {
            const orgStore = useOrgStore();

            await axios.delete(`${orgStore.api}/saved-views/${view.id}`);
            await this.load(view.list_key, { force: true });
        }
    }
});
